import classNames from "classnames";
import { FormikProps } from "formik";
import moment from "moment";
import { useCallback, useContext, useMemo } from "react";
import {
  BlankMediaPeriodInfo,
  BlankMediaTypeCommonDataViewModel,
} from "../../../../../generated/data-contracts";
import {
  IBlankMediaTypeCommonData,
  IUrlParamsBlankMedia,
} from "../../../../../models";
import { isMobileView } from "../../../../shared/appStates/appStates";
import {
  AvisErrorMessage,
  AvisSection,
  AvisValueRow,
} from "../../../../shared/components/form";
import { AvisCalendar } from "../../../../shared/components/inputs";
import { AvisContext } from "../../../../shared/context";
import { AvisConstants } from "../../../../../data/avisConstants";

interface ICommonDataProps {
  children: React.ReactNode;
  applicationData: BlankMediaTypeCommonDataViewModel | undefined;
  bag: FormikProps<IBlankMediaTypeCommonData>;
  periodInfo: BlankMediaPeriodInfo | undefined;
  sectionsVisible: boolean | undefined;
  title: string;
  textToDisplay: string;
  firstLoad: boolean | undefined;
  loading: boolean | undefined;
  urlParams: IUrlParamsBlankMedia;
  disabled?: boolean;
  onPeriodChange?: (dateFrom?: Date, dateTo?: Date) => void;
}

export function CommonData(props: Readonly<ICommonDataProps>) {
  const {
    children,
    applicationData,
    bag,
    periodInfo,
    title,
    textToDisplay,
    urlParams,
    disabled,
    onPeriodChange,
  } = props;
  const { res } = useContext(AvisContext);

  const widthClass = isMobileView.value ? "col-12" : "col-10";
  const classNameCommonData = classNames("blank-media-common-data", widthClass);
  const defaultDate = useMemo(
    () => moment().add(-1, "month").startOf("month").toDate(),
    [],
  );

  const onDateChange = useCallback(
    (e: { originalEvent: Event; value: Date | Date[]; target: any }) => {
      const valueSelected = e.value as Date;
      if (!valueSelected) {
        bag.setFieldValue("DateTo", undefined);
        //Callback on date changed
        onPeriodChange?.(undefined, undefined);
        return;
      }
      const valueSelectedMoment = moment(valueSelected).utc(true);
      const dateFrom = valueSelectedMoment.startOf("month");
      const dateTo = valueSelectedMoment.clone().endOf("month").startOf("day");

      bag.setFieldValue(
        "dateFrom",
        dateFrom.format(AvisConstants.date_format_ISO),
      );
      bag.setFieldValue("dateTo", dateTo.format(AvisConstants.date_format_ISO));

      //Callback on date changed
      onPeriodChange?.(dateFrom.toDate(), dateTo.toDate());
    },
    [bag, onPeriodChange],
  );

  const renderPeriodValidationError = useMemo(() => {
    if (!periodInfo) return;

    const activeRoleTextKey =
      urlParams.applicationType == "ProfessionalUser"
        ? "blankMedia_Application_validation_NoRoleAtPeriod_P"
        : "blankMedia_Application_validation_NoRoleAtPeriod_I";
    if (!periodInfo.personHasActiveRole)
      return <AvisErrorMessage value={res(activeRoleTextKey)} />;

    if (periodInfo.periodHasApplication)
      return (
        <AvisErrorMessage
          value={res("blankMedia_Application_validation_AlreadyHasApplication")}
        />
      );
  }, [periodInfo, res, urlParams]);

  const renderPeriodRows = useMemo(
    () => (
      <>
        <AvisValueRow
          rowTitle={res("blankMedia_Application_label_PeriodFrom")}
          rowValue={
            <>
              <AvisCalendar<IBlankMediaTypeCommonData>
                className="period-date-input"
                bag={bag}
                bagFieldAsISOstring="dateFrom"
                disabled={disabled}
                maxDate={moment().endOf("month").toDate()}
                yearRange={`${moment().year() - 1}:${moment().year()}`}
                defaultDateToShow={defaultDate}
                onChange={onDateChange}
                hideClear
              />
              {renderPeriodValidationError}
            </>
          }
        />
        <AvisValueRow
          rowTitle={res("blankMedia_Application_label_PeriodTo")}
          rowValue={
            <AvisCalendar<IBlankMediaTypeCommonData>
              className="period-date-input"
              bag={bag}
              bagFieldAsISOstring="dateTo"
              disabled={true}
              hideClear
            />
          }
        />
      </>
    ),
    [
      bag,
      defaultDate,
      disabled,
      onDateChange,
      renderPeriodValidationError,
      res,
    ],
  );
  const renderApplicationDataRows = useMemo(() => {
    if (!applicationData) return;
    return (
      <>
        <AvisValueRow
          rowTitle={res("label_Number")}
          rowValue={applicationData?.documentNumber}
        />
        <AvisValueRow
          rowTitle={res("label_Status")}
          rowValue={applicationData?.portalReportStatusType.shortName}
        />
        {applicationData?.portalReportStatusType.value === "Rejected" && (
          <AvisValueRow
            rowTitle={res("label_RejectReason")}
            rowValue={applicationData?.rejectedReason}
          />
        )}
      </>
    );
  }, [applicationData, res]);

  const renderDealNotOccuredCheckbox = useMemo(() => {
    if (!children) return;

    return <AvisValueRow rowTitle="" rowValue={children} />;
  }, [children]);

  return (
    <AvisSection title={title} textToDisplayKey={textToDisplay}>
      <div className={classNameCommonData}>
        {renderPeriodRows}
        {renderApplicationDataRows}
        {renderDealNotOccuredCheckbox}
      </div>
    </AvisSection>
  );
}
